/**
 * The line a draft is shown by in the draft list: `Diameter 6 · Längd 76 · Beläggning TiAlN`.
 *
 * <p><b>The fields are the configurator's first summary block's</b> (the root's before the steps',
 * in schema order), the ones the author has answered or the run decided, in the block's order — the
 * author has already chosen what sums a configuration up. With no summary block, or one that names no answered field,
 * the first three answered fields in schema order stand in. When the run assumed one of the answers shown,
 * the line ends with `Antaget: enda raden kvar`, once.</p>
 *
 * <p><b>A dropdown is shown by its option's text, not its value</b>, and that text exists only in an
 * evaluation's state — which is why the open page writes the line and the list only reads it. Pinned
 * by `draft-summary.test.ts`.</p>
 */

// Deep path, not the barrel: this module's test runs in the `logik` project.
import { picksFromAList } from '@ixit/dof-core/src/list-kinds.js';

/** The field kinds an author answers; a Result is computed and sums up nothing he did. */
const ANSWERED_KINDS = new Set(['Numeric', 'Dropdown', 'TableChoice', 'Text']);

/** How many answered fields stand in when no summary block names one. */
const FALLBACK_FIELD_COUNT = 3;

interface AnsweredField {
    label: string;
    dataType: string;
}

function isBlank(value: unknown): boolean {
    return value === undefined || value === null || value === '';
}

/** The answerable fields by alias, in schema order (a step's fields, then its sub-steps). */
function answerableFields(schema: any): Map<string, AnsweredField> {
    const fields = new Map<string, AnsweredField>();
    const walk = (step: any) => {
        for (const field of step?.fields ?? []) {
            if (field?.alias && ANSWERED_KINDS.has(field.dataType) && !fields.has(field.alias)) {
                fields.set(field.alias, { label: field.label || field.alias, dataType: field.dataType });
            }
        }
        for (const sub of step?.steps ?? []) walk(sub);
    };
    for (const step of schema?.steps ?? []) walk(step);
    return fields;
}

/** The first summary block: the root's, then each step's in schema order. */
function firstSummaryBlock(schema: any): any | null {
    if ((schema?.summaries ?? []).length > 0) return schema.summaries[0];
    const walk = (steps: any[]): any | null => {
        for (const step of steps ?? []) {
            if ((step?.summaries ?? []).length > 0) return step.summaries[0];
            const nested = walk(step?.steps ?? []);
            if (nested) return nested;
        }
        return null;
    };
    return walk(schema?.steps ?? []);
}

/** The evaluated field by alias, anywhere in the state's step tree. */
function evaluatedField(state: any, alias: string): any | null {
    const walk = (steps: any[]): any | null => {
        for (const step of steps ?? []) {
            const hit = (step?.fields ?? []).find((f: any) => f?.alias === alias);
            if (hit) return hit;
            const nested = walk(step?.steps ?? []);
            if (nested) return nested;
        }
        return null;
    };
    return walk(state?.steps ?? []);
}

function shown(value: unknown, field: AnsweredField, alias: string, state: any): string {
    if (picksFromAList(field.dataType)) {
        const option = (evaluatedField(state, alias)?.options ?? [])
            .find((o: any) => String(o?.value ?? '') === String(value));
        if (option?.label) return String(option.label);
    }
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

/**
 * The summary line for these answers, or null when nothing has been answered.
 *
 * @param schema the render schema the page stands on
 * @param state the evaluation that answered `inputs` — the dropdowns' option texts come from it
 * @param inputs alias → value
 */
export function draftSummary(schema: any, state: any, inputs: Record<string, unknown>): string | null {
    const fields = answerableFields(schema);
    const answers = { ...inputs, ...decidedByTheRun(state, fields) };
    const answered = (alias: string) => fields.has(alias) && !isBlank(answers[alias]) && !takenBack(state, alias)
        && !waiting(state, alias);

    const block = firstSummaryBlock(schema);
    const named = block
        ? [...new Set<string>((block.groups ?? []).flatMap((g: any) => g?.fieldAliases ?? []))].filter(answered)
        : [];
    const aliases = named.length > 0
        ? named
        : [...fields.keys()].filter(answered).slice(0, FALLBACK_FIELD_COUNT);

    if (aliases.length === 0) return null;
    const line = aliases
        .map((alias) => `${fields.get(alias)!.label} ${shown(answers[alias], fields.get(alias)!, alias, state)}`)
        .join(' · ');
    return aliases.some((alias) => assumedAsTheOnlyRowLeft(state, alias)) ? `${line} · ${ASSUMED_ONLY_ROW_LEFT}` : line;
}

/** Said once at the end of the line, after the answers, when one of the answers it shows the run assumed. */
const ASSUMED_ONLY_ROW_LEFT = 'Antaget: enda raden kvar';

/** Whether the run assumed this answer because only one row was left; a reason without words here says
 *  nothing. Pinned by `draft-summary.test.ts`, "a line with an answer the run assumed". */
function assumedAsTheOnlyRowLeft(state: any, alias: string): boolean {
    return evaluatedField(state, alias)?.assumed?.reason === 'onlyRowLeft';
}

/** Whether the run took back an answer it once assumed: the draft still holds it, but it does not count, so it
 *  sums up nothing. Pinned by `draft-summary.test.ts`, "an assumed answer the run took back". */
function takenBack(state: any, alias: string): boolean {
    const failures = evaluatedField(state, alias)?.validationFailures;
    return Array.isArray(failures) && failures.some((f: any) => f?.source === 'Assumption');
}

/** Whether the field waits for another choice first: the draft still holds its old value, but until that
 *  choice is made the value is not part of the configuration, so it sums up nothing. Pinned by
 *  `draft-summary.test.ts`, "a value whose field waits for another choice". */
function waiting(state: any, alias: string): boolean {
    return evaluatedField(state, alias)?.status === 'AwaitingInput';
}

/** The values the run decided itself, one option being left: they sum up the configuration as an answer
 *  does, though the author never gave them. */
function decidedByTheRun(state: any, fields: Map<string, AnsweredField>): Record<string, unknown> {
    const decided: Record<string, unknown> = {};
    for (const alias of fields.keys()) {
        const field = evaluatedField(state, alias);
        if (field?.answeredByTheRun === true && !isBlank(field.value)) decided[alias] = field.value;
    }
    return decided;
}

/**
 * When a draft was last written, as the list says it: `i dag 14:32`, `i går 09:05`, or the date.
 * A timestamp without a zone is read as UTC, which is what the wire carries.
 */
export function draftWhen(iso: string, now: Date = new Date()): string {
    const at = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
    if (Number.isNaN(at.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, '0');
    const time = `${pad(at.getHours())}:${pad(at.getMinutes())}`;
    const day = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (day(at) === day(now)) return `i dag ${time}`;
    if (day(at) === day(yesterday)) return `i går ${time}`;
    return `${day(at)} ${time}`;
}
